import React, { useState, useEffect, useRef, memo } from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Switch,
  FormControlLabel,
  Collapse,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useApi } from "../../../services/useApi";
import { ApiSuccessResponse, ApiErrorResponse } from "../../../types/api";
import {
  ExpandLess,
  ExpandMore,
  Delete as DeleteIcon,
  Save as SaveIcon,
  FolderOpen as FolderOpenIcon,
  Update as UpdateIcon,
  Block as BlockIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import DataTable from "../../../components/common/DataTable";
import MappingCell from "../components/MappingCell";
import MappingDialogs from "../components/MappingDialogs";
import { deepClean } from "../../../utils/objectUtils";
import { useCommonOptions } from "../../../hooks/useCommonOptions";
import SubmissionReportDialog, {
  SubmissionReport,
  BatchError,
} from "../components/SubmissionReportDialog";
import { useDynamicMappings } from "../../../hooks/useDynamicMappings";
import ValueCell from "../components/ValueCell";
import OneSnackbar from "../components/OneSnackbar";
import SubmitOptions from "../components/SubmitOptions";
import {
  BatchRecordError,
  MappingSubmitResponse,
  UpdateIfExistsMap,
} from "../../../interfaces/CommonInterface";
import { scrollToErrorRow, validateMapping } from "../../../hooks/useCommonValidate";
import UploadData from "../components/UploadData";
import { useAutoMapping } from "../../../hooks/useAutoMapping";
import DataSourceTable from "../components/DataSourceTable";

interface EconomicSupplier {
  index?: number;
  address?: string; // Address for the supplier including street and number.
  attention?: {
    supplierContactNumber?: string; // Unique number identifying the supplier contact.
  };
  bankAccount?: string; // The supplier's bank account.
  barred?: string; // Boolean indication of whether the supplier is barred.
  city?: string; // The supplier's city.
  corporateIdentificationNumber?: string; // Company Identification Number. For example CVR in Denmark.
  country?: string;
  currency?: string;
  defaultInvoiceText?: string;
  email?: string; // Email address of the supplier.
  layout?: {
    layoutNumber?: string; // Unique number identifying the layout.
  };
  name?: string; // Name of the supplier.
  paymentTerms?: {
    paymentTermsNumber?: string; // Unique number identifying the payment terms.
    originalValue?: string;
  };
  phone?: string; // Phone number of the supplier.
  salesPerson?: {
    employeeNumber?: string; // Unique number identifying the sales person.
  };
  supplierContact?: {
    supplierContactNumber?: string; // Unique number identifying the supplier contact.
  };
  supplierGroup?: {
    supplierGroupNumber?: string; // Unique number identifying the supplier group.
  };
  supplierNumber?: string; // Unique number identifying the supplier.
  vatZone?: {
    vatZoneNumber?: string; // Unique number identifying the VAT zone.
    originalValue?: string;
  };
  zip?: string; // Zip code for the supplier.
}

interface SupplierPanelProps {}

// type CustomerPanelResponse =
//   | ApiSuccessResponse<{
//       Collection: DineroContact[];
//       Pagination: Pagination;
//     }>
//   | ApiErrorResponse;

interface Mapping {
  address: string;
  attention_supplierContactNumber: string;
  bankAccount: string;
  barred: string;
  city: string;
  corporateIdentificationNumber: string;
  country: string;
  currency: string;
  defaultInvoiceText: string;
  email: string;
  layout_layoutNumber: string;
  name: string;
  paymentTerms_paymentTermsNumber: string;
  phone: string;
  salesPerson_employeeNumber: string;
  supplierContact_supplierContactNumber: string;
  supplierGroup_supplierGroupNumber: string;
  supplierNumber: string;
  vatZone_vatZoneNumber: string;
  zip: string;
}

interface DefaultValues extends Mapping {}

const SupplierPanel: React.FC<SupplierPanelProps> = () => {
  const [economicContacts, setEconomicContacts] = useState<EconomicSupplier[]>(
    []
  );
  const [sourceColumns, setSourceColumns] = useState<
  { id: string; label: string }[]
>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [economicLoading, setEconomicLoading] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | string[] | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping>({
    address: "",
    attention_supplierContactNumber: "",
    bankAccount: "",
    barred: "",
    city: "",
    corporateIdentificationNumber: "",
    country: "",
    currency: "",
    defaultInvoiceText: "",
    email: "",
    layout_layoutNumber: "",
    name: "",
    paymentTerms_paymentTermsNumber: "",
    phone: "",
    salesPerson_employeeNumber: "",
    supplierContact_supplierContactNumber: "",
    supplierGroup_supplierGroupNumber: "",
    supplierNumber: "",
    vatZone_vatZoneNumber: "",
    zip: "",
  });
  const [batchSize] = useState(10);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(0);
  const [shouldStop, setShouldStop] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [updateIfExistsMap, setUpdateIfExistsMap] = useState<UpdateIfExistsMap>(
    {}
  );

  const [defaultValues, setDefaultValues] = useState<DefaultValues>({
    address: "",
    city: "",
    corporateIdentificationNumber: "",
    country: "",
    currency: "",
    supplierNumber: "",
    email: "",
    name: "",
    zip: "",
    attention_supplierContactNumber: "",
    layout_layoutNumber: "",
    paymentTerms_paymentTermsNumber: "",
    salesPerson_employeeNumber: "",
    supplierContact_supplierContactNumber: "",
    supplierGroup_supplierGroupNumber: "",
    vatZone_vatZoneNumber: "",
    bankAccount: "",
    barred: "",
    defaultInvoiceText: "",
    phone: "",
  });
  const [updateExistingCustomer, setUpdateExistingCustomer] =
    useState<boolean>(false);
  const [requiredFields] = useState<Array<keyof Mapping>>([
    "name",
    "paymentTerms_paymentTermsNumber",
    "vatZone_vatZoneNumber",
    "supplierGroup_supplierGroupNumber",
    "currency",
  ]);
  const [updateKey, setUpdateKey] = useState<string>("");
  const [showReport, setShowReport] = useState(false);
  const [submissionReport, setSubmissionReport] =
    useState<SubmissionReport | null>(null);
  const [errorRecords, setErrorRecords] = useState<Set<string>>(new Set());
  const tableRef = useRef<HTMLDivElement>(null);

  const api = useApi();
  const {
    currencies,
    paymentTerms,
    vatZones,
    customerGroups,
    supplierGroups,
    isLoading: isLoadingOptions,
  } = useCommonOptions();
  const { getMappedValue: getDynamicMappedValue, customMappings } =
    useDynamicMappings();
  const [skipLines, setSkipLines] = useState<number>(0);
  const [sourceData, setSourceData] = useState<any[][]>([]);
  const [originalSourceData, setOriginalSourceData] = useState<any[][]>([]);
  const columnMappings = {
    address: ["address", "street", "street address", "Adresse1"],
    // attention_supplierContactNumber: ["attention", "attention number", "attention number", "attention number", "attention number", "attention number",'Attention','AttentionNo'],
    bankAccount: ["bank account", "bank account number", "bank account number", "bank account number", "bank account number", "bank account number",'BankKonto','BankAccount'],
    // barred: ["barred", "barred", "barred", "barred", "barred", "barred"],
    city: ["city", "town", "ByNavn", "By",'TownCity'],
    corporateIdentificationNumber: ['CVR-nr.','Co. reg. no.',"corporate identification number", "corporate identification number", "corporate identification number", "corporate identification number", "corporate identification number", "corporate identification number"],
    country: ["country", "nation", "Land"],
    currency: ["currency",'Valuta'],
    // defaultInvoiceText: ["default invoice text"],
    email: ["email"],
    // layout_layoutNumber: ["layout"],
    name: ["name",'navn','Navn'],
    paymentTerms_paymentTermsNumber: ["payment terms",'BetalingsBetingelser','PaymentTerms'],
    phone: ["phone"],
    // salesPerson_employeeNumber: ["sales person"],
    // supplierContact_supplierContactNumber: ["supplier contact"],
    supplierGroup_supplierGroupNumber: ["supplier group",'SupplierGroup','Group','LeverandoerGruppe'],
    supplierNumber: ["supplier","No.","number",'SupplierNo','SupplierNr','LeverandoerNr','Supplier No'],
    vatZone_vatZoneNumber: ["vat zone",'Zone','Vatzone'],
    zip: ["zip",'Postcode','PostNr','Postnummer'],
  }


  const { handleAutoMapFirstRow, autoMappingDone, setAutoMappingDone } =
    useAutoMapping<Mapping>({
      sourceData,
      originalSourceData,
      mapping,
      setMapping,
      setSourceColumns,
      setSuccess,
      setSkipLines,
      onSourceDataUpdate: (newData) => {
        setSourceData(newData);
        setTotalCount(newData.length);
      },
      columnMappings,
    });


  useEffect(() => {
    if (autoMappingDone && sourceData.length > 0) {
      mapSourceToEconomic(sourceData);
      setAutoMappingDone(false);
    }
  }, [mapping, autoMappingDone, sourceData]);

  const handleSkipLinesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(0, parseInt(e.target.value) || 0);
    setSkipLines(value);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDefaultValue = (field: keyof DefaultValues) => {
    return defaultValues[field];
  };

  const mapSourceToEconomic = (sourceData: any[][]) => {
    const dataToProcess = sourceData.slice(skipLines);
    const mappedContacts = dataToProcess.map((row, index) => {
      function getMappedValue(field: string): {
        mapped: string | null;
        original: string | null;
      } {
        const def = getDefaultValue(field as keyof DefaultValues);
        if (def) return { mapped: def, original: null };

        if (field in mapping) {
          const key = mapping[field];
          if (key) {
            // Convert key to column index (assuming key is in format "col0", "col1", etc.)
            const colIndex = parseInt(key.replace("col", ""));
            if (!isNaN(colIndex) && colIndex < row.length) {
              const raw = row[colIndex];
              const mapped = getDynamicMappedValue(field, raw);
              return { mapped, original: raw };
            }
          }
        }

        return { mapped: null, original: null };
      }
      const {
        mapped: paymentTermsNumber,
        original: paymentTermsNumberOriginal,
      } = getMappedValue("paymentTerms_paymentTermsNumber");
      const { mapped: vatZoneNumber, original: vatZoneNumberOriginal } =
        getMappedValue("vatZone_vatZoneNumber");
      // const { mapped: supplierGroupSupplierGroupNumber, original: supplierGroupSupplierGroupNumberOriginal } = getMappedValue("supplierGroup_supplierGroupNumber");
      // const { mapped: layoutLayoutNumber, original: layoutLayoutNumberOriginal } = getMappedValue("layout_layoutNumber");

      return {
        index: index + skipLines,
        address: getMappedValue("address").mapped,
        attention: {
          supplierContactNumber: getMappedValue(
            "attention_supplierContactNumber"
          ).mapped,
        },
        bankAccount: getMappedValue("bankAccount").mapped,
        barred: getMappedValue("barred").mapped,
        city: getMappedValue("city").mapped,
        corporateIdentificationNumber: getMappedValue(
          "corporateIdentificationNumber"
        ).mapped,

        country: getMappedValue("country").mapped,
        currency: getMappedValue("currency").mapped,
        defaultInvoiceText: getMappedValue("defaultInvoiceText").mapped,
        email: getMappedValue("email").mapped,
        layout: {
          layoutNumber: getMappedValue("layout_layoutNumber").mapped,
        },
        name: getMappedValue("name").mapped,
        paymentTerms: {
          paymentTermsNumber: paymentTermsNumber,
          originalValue: paymentTermsNumberOriginal,
        },
        phone: getMappedValue("phone").mapped,
        salesPerson: {
          employeeNumber: getMappedValue("salesPerson_employeeNumber").mapped,
        },
        supplierContact: {
          supplierContactNumber: getMappedValue(
            "supplierContact_supplierContactNumber"
          ).mapped,
        },
        supplierGroup: {
          supplierGroupNumber: getMappedValue(
            "supplierGroup_supplierGroupNumber"
          ).mapped,
        },
        supplierNumber: getMappedValue("supplierNumber").mapped,
        vatZone: {
          vatZoneNumber: vatZoneNumber,
          originalValue: vatZoneNumberOriginal,
        },
        zip: getMappedValue("zip").mapped,
      };
    });
    setEconomicContacts(mappedContacts);
    setSuccess("Successfully mapped " + mappedContacts.length + " contacts");
  };

  const handleSubmitMapping = async () => {
    // Validate required fields
    const validationErrors = validateMapping(
      mapping,
      defaultValues,
      requiredFields
    );
    if (validationErrors.length > 0) {
      setError(validationErrors);
      return;
    }

    if (updateExistingCustomer && !updateKey) {
      setError("Please select a key for updating existing suppliers");
      return;
    }

    setIsSubmitting(true);
    setSubmitProgress(0);
    setError(null);
    setSuccess(null);
    setErrorRecords(new Set()); // Reset error records
    setShouldStop(false); // Reset stop flag
    setProcessedCount(0); // Reset processed count

    // Create new AbortController for this submission
    abortControllerRef.current = new AbortController();

    const totalRecords = economicContacts.length;
    const batches = Math.ceil(totalRecords / batchSize);
    setTotalBatches(batches);

    let successCount = 0;
    let failedCount = 0;
    let allErrors: (string | BatchRecordError)[] = [];

    try {
      for (let i = 0; i < batches; i++) {
        // Check if should stop
        if (shouldStop) {
          break;
        }

        const start = i * batchSize;
        const end = Math.min(start + batchSize, totalRecords);
        const batchContacts = economicContacts.slice(start, end);

        const filteredBatch = batchContacts
          .map((contact) => deepClean(contact))
          .filter((item) => item && Object.keys(item).length > 0);

        const response = await api.post<MappingSubmitResponse>(
          "/main/create-supplier",
          {
            mapping,
            contacts: filteredBatch,
            batchInfo: {
              currentBatch: i + 1,
              totalBatches: batches,
            },
            updateIfExists: updateIfExistsMap,
          },
          {
            signal: abortControllerRef.current.signal,
          }
        );

        const batchResponse = response.data.data;
        successCount += batchResponse.successCount;
        failedCount += batchResponse.failedCount;
        setProcessedCount(successCount); // Update processed count

        if (batchResponse.errors && batchResponse.errors.length > 0) {
          allErrors = [...allErrors, ...batchResponse.errors];
          batchResponse.errors.forEach((error) => {
            setErrorRecords((prev) => new Set(prev).add(error.recordId));
          });
        }

        setCurrentBatch(i + 1);
        setSubmitProgress(Math.round(((i + 1) / batches) * 100));
      }

      // Only process errors and show report if not stopped
      if (!shouldStop) {
        const reportErrors: (string | BatchError)[] = [];
        allErrors.forEach((error) => {
          if (typeof error === "string") {
            reportErrors.push(error);
          } else {
            error.errors.forEach((err) => {
              reportErrors.push({
                recordId: error.recordId,
                field: "",
                message: err,
                value: "",
              });
            });
          }
        });

        setSubmissionReport({
          successCount,
          failedCount,
          errors: reportErrors,
        });
        setShowReport(true);

        if (reportErrors.length > 0) {
          setError(
            `Failed to process ${failedCount} records. Click "View Report" for details.`
          );
        } else {
          const successMessage = `Successfully processed ${successCount} records.`;
          setSuccess(successMessage);
        }
      }
    } catch (error) {
      if (error.status === 500 && error.message === "canceled") {
        setShowReport(true);
        setShouldStop(false);
        // Don't show message here as it's already shown in handleStopSubmit
      } else {
        setError("Failed to submit mapping. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
      setSubmitProgress(0);
      abortControllerRef.current = null;
    }
  };

  const handleUpdateIfExistsChange = (field: string, value: boolean) => {
    setUpdateIfExistsMap((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleStopSubmit = () => {
    setShouldStop(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Show stop message immediately
    setSuccess(
      `Process stopped. Successfully processed ${processedCount} records.`
    );
    setIsSubmitting(false);
    setSubmitProgress(0);
    abortControllerRef.current = null;
  };

  return (
    <>
      <UploadData
        onLoading={(loading) => {
          setLoading(loading);
        }}
        onLoadData={(data, columns) => {
          setOriginalSourceData(data);
          setSourceData(data);
          setSourceColumns(columns);
          setTotalCount(data.length);
          mapSourceToEconomic(data.slice(skipLines));
        }}
        onError={(msg) => {
          setError(msg);
        }}
      />
      <MappingDialogs
        mapping={mapping}
        defaultValues={defaultValues}
        type="supplier"
        onApplyMapping={(newMap, defaults) => {
          setMapping(newMap);
          setDefaultValues(defaults);
        }}
        onSuccess={(msg) => setSuccess(msg)}
      />

      <SubmitOptions
        mapSourceDataToEconomic={mapSourceToEconomic}
        sourceData={sourceData}
        economicData={economicContacts}
        isSubmitting={isSubmitting}
        handleSubmitMapping={handleSubmitMapping}
        handleStopSubmit={handleStopSubmit}
        submissionReport={submissionReport}
        currentBatch={currentBatch}
        totalBatches={totalBatches}
        submitProgress={submitProgress}
        setShowReport={setShowReport}
        skipLines={skipLines}
        handleSkipLinesChange={handleSkipLinesChange}
        onAutoMapFirstRow={handleAutoMapFirstRow}
      />

    <DataSourceTable
        sourceColumns={sourceColumns}
        sourceData={sourceData}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        skipLines={skipLines}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
      />
      <Typography
        variant="h5"
        sx={{ mb: 2, fontWeight: "bold", color: "primary.main", mt: 2 }}
      >
        E-conomic Suppliers
      </Typography>

      <TableContainer component={Paper} sx={{ maxHeight: 440 }} ref={tableRef}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "auto",
            "& th, & td": {
              whiteSpace: "nowrap",
            },
          }}
          aria-label="economic supplier table"
        >
          <TableHead>
            <TableRow>
              <MappingCell
                key="supplierNumber"
                label="Supplier No."
                field="supplierNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange("supplierNumber", value)
                }
              />
              <MappingCell
                key="supplierGroup"
                label="Group"
                field="supplierGroup_supplierGroupNumber"
                mapping={mapping}
                setMapping={setMapping}
                defaultValues={defaultValues}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={supplierGroups}
              />

              <MappingCell
                key="name"
                label="Name"
                field="name"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange("name", value)
                }
              />

              <MappingCell
                label="Address"
                field="address"
                key="address"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
              <MappingCell
                label="Zip"
                field="zip"
                key="zip"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />

              <MappingCell
                label="City"
                field="city"
                key="city"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />

              <MappingCell
                label="Country"
                field="country"
                key="country"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                // itemOptions={countryOptions}
              />

              <MappingCell
                label="Email"
                field="email"
                key="email"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange("email", value)
                }
              />

              <MappingCell
                key="currency"
                label="Currency"
                field="currency"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={currencies}
              />

              <MappingCell
                key="vatZone"
                label="Vatzone"
                field="vatZone_vatZoneNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={vatZones}
              />
              <MappingCell
                key="paymentTerms"
                label="Payment Terms"
                field="paymentTerms_paymentTermsNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                itemOptions={paymentTerms}
              />

              <MappingCell
                label="Co. reg. no."
                field="corporateIdentificationNumber"
                key="corporateIdentificationNumber"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
                onUpdateIfExistsChange={(value) =>
                  handleUpdateIfExistsChange(
                    "corporateIdentificationNumber",
                    value
                  )
                }
              />

              <MappingCell
                label="Phone"
                field="phone"
                key="phone"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />

              <MappingCell
                label="Bank Account"
                field="bankAccount"
                key="bankAccount"
                mapping={mapping}
                defaultValues={defaultValues}
                setMapping={setMapping}
                setDefaultValues={setDefaultValues}
                options={sourceColumns}
                requiredFields={requiredFields}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={14} align="center" sx={{ padding: "8px" }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : economicContacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14} align="center" sx={{ padding: "8px" }}>
                  <Typography variant="body2">No data available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              economicContacts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((contact) => {
                  const hasError = errorRecords.has(
                    contact?.index?.toString() || ""
                  );
                  return (
                    <TableRow
                      key={contact.index}
                      id={`row-${contact.index}`}
                      sx={{
                        backgroundColor: hasError
                          ? "rgba(255, 0, 0, 0.1)"
                          : undefined,
                        "&:hover": {
                          backgroundColor: hasError
                            ? "rgba(255, 0, 0, 0.2)"
                            : undefined,
                        },
                      }}
                    >
                      <ValueCell value={contact.supplierNumber} />
                      <ValueCell
                        value={contact.supplierGroup?.supplierGroupNumber}
                        options={supplierGroups}
                      />
                      <ValueCell value={contact.name} />
                      <ValueCell value={contact.address} />
                      <ValueCell value={contact.zip} />
                      <ValueCell value={contact.city} />
                      <ValueCell value={contact.country} />
                      <ValueCell value={contact.email} />
                      <ValueCell
                        value={contact.currency}
                        options={currencies}
                      />

                      <ValueCell
                        value={contact.vatZone.vatZoneNumber}
                        originalValue={contact.vatZone.originalValue}
                        options={vatZones}
                      />
                      <ValueCell
                        value={contact.paymentTerms?.paymentTermsNumber}
                        originalValue={contact.paymentTerms?.originalValue}
                        options={paymentTerms}
                      />

                      <ValueCell
                        value={contact.corporateIdentificationNumber}
                      />
                      <ValueCell value={contact.phone} />
                      {/* <ValueCell value={contact.salesPerson?.employeeNumber}/> */}
                      {/* <ValueCell value={contact.supplierContact?.supplierContactNumber}/> */}
                      <ValueCell value={contact.bankAccount} />
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            zIndex: 1,
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.75rem",
              },
          }}
        />
      </TableContainer>

      <OneSnackbar
        error={error}
        success={success}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
      />

      <SubmissionReportDialog
        open={showReport}
        onClose={() => setShowReport(false)}
        report={submissionReport}
        onErrorClick={(recordId) => {
          scrollToErrorRow(recordId, economicContacts, rowsPerPage, page, setPage, tableRef);
        }}
      />
    </>
  );
};

export default memo(SupplierPanel);
